import { useQuery } from "@tanstack/react-query";
import { eventService } from "../services/event.service";
import { SourceCheckVerdict } from "../domain/measurement.interface";

/**
 * The verdict for each of an event's measurement sources — SIMS-9281 AC2 / AC4.
 *
 * 🔴 LIVE, never stored (decision E3-03). The question is keyed by the event and
 * the sorted set of sources, so committing a changed source asks again and
 * reopening the event asks again — a warning can never be latched from an
 * earlier answer, and a corrected source clears it.
 *
 * An event that does not exist yet has no records, so every source reads
 * "not checked yet" without a request (Q-7 refinement 2). While the server has
 * not answered, or if it fails, the map has no entry for that source: the
 * screen shows nothing rather than invent a verdict.
 *
 * @returns source -> verdict
 */
export const useMeasurementSourceCheck = (
	eventId: string | null | undefined,
	sources: string[],
): Record<string, SourceCheckVerdict> => {
	const unique = Array.from(new Set(sources)).sort();

	const { data } = useQuery({
		queryKey: ["sims-event-measurement-sources", eventId, unique],
		queryFn: () =>
			eventService.checkMeasurementSources(eventId as string, unique),
		enabled: Boolean(eventId) && unique.length > 0,
		// Deliberate: a new request comes from a new key (another event, or a
		// changed set of sources) or a remount when the event is reopened — never
		// from focus. staleTime 0 makes that remount ask again instead of showing
		// an earlier answer.
		staleTime: 0,
		refetchOnWindowFocus: false,
		retry: false,
	});

	if (!eventId) {
		return Object.fromEntries(
			unique.map((source) => [source, SourceCheckVerdict.NO_RECORDS]),
		);
	}

	return Object.fromEntries(
		(data ?? []).map(({ source, verdict }) => [source, verdict]),
	);
};
